import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/App";
import { ArrowLeft, Globe, Mail, Phone, MapPin, BadgeCheck, Building2 } from "lucide-react";
import { Seo } from "@/components/Seo";


export default function ProfessionalPublic() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/professionals/${userId}`)
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e?.response?.data?.detail || "Profilo non disponibile"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [userId]);

  const doFollow = async () => {
    setBusy(true);
    try {
      await api.post(`/professional/${userId}/follow`, {});
      window.dispatchEvent(new Event('ade-following-changed'));
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore durante il collegamento");
    } finally { setBusy(false); }
  };

  const doUnfollow = async () => {
    if (!window.confirm("Rimuovere il collegamento a questo profilo ADE Pro?")) return;
    setBusy(true);
    try {
      await api.delete(`/professional/${userId}/follow`);
      window.dispatchEvent(new Event('ade-following-changed'));
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const trackClick = (kind, href) => {
    // Best-effort — analytics is Phase 3. For now just navigate.
    if (kind === "website" && href) window.open(href, "_blank", "noopener,noreferrer");
    if (kind === "email" && href) window.location.href = `mailto:${href}`;
    if (kind === "phone" && href) window.location.href = `tel:${href}`;
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Caricamento…</div>;
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-gray-700">{error || "Profilo non trovato"}</p>
        <button onClick={() => navigate(-1)} className="text-blue-600 underline" data-testid="pro-back-error">Indietro</button>
      </div>
    );
  }

  const pp = data.professional_profile || {};
  const websiteUrl = pp.website_url && !/^https?:\/\//.test(pp.website_url) ? `https://${pp.website_url}` : pp.website_url;

  return (
    <div className="min-h-screen bg-white" data-testid="pro-public-page">
      <Seo
        title={`${pp.professional_name || data.name} · ADE`}
        description={pp.extended_bio?.slice(0, 160) || `${pp.professional_name} su ADE`}
        canonical={`${window.location.origin}/app/pro/${userId}`}
      />

      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b flex items-center gap-3 px-4 py-3">
        <button onClick={() => navigate(-1)} className="p-1" data-testid="pro-back">
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <span className="text-sm font-semibold text-gray-800 truncate flex-1">{pp.professional_name || data.name}</span>
      </div>

      {/* Cover */}
      <div className="h-32 bg-gradient-to-br from-blue-100 via-cyan-50 to-white" />

      {/* Identity block */}
      <div className="px-5 -mt-10">
        <div className="flex items-end gap-3">
          <img
            src={data.picture || "/images/default-avatar.png"}
            alt={data.name}
            className="w-20 h-20 rounded-2xl object-cover border-4 border-white shadow-md bg-gray-100"
            data-testid="pro-avatar"
          />
          {/* Pro badge (distinct from verified / creator / golden) */}
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200"
            title="ADE Pro"
            data-testid="pro-badge"
          >
            <Building2 className="w-3 h-3" /> PRO
          </span>
          {data.is_verified && (
            <BadgeCheck className="w-5 h-5 text-blue-500" title="Verificato" />
          )}
        </div>

        <h1 className="mt-3 text-xl font-bold text-gray-900" data-testid="pro-name">
          {pp.professional_name || data.name}
        </h1>
        {data.username && (
          <p className="text-sm text-gray-500" data-testid="pro-username">@{data.username}</p>
        )}
        <p className="mt-1 text-xs uppercase tracking-wide text-blue-700 font-semibold" data-testid="pro-category">
          {pp.professional_type ? `${pp.professional_type} · ` : ""}
          {pp.category}
          {pp.subcategory ? ` · ${pp.subcategory}` : ""}
        </p>

        {pp.extended_bio && (
          <p className="mt-3 text-sm text-gray-700 whitespace-pre-wrap" data-testid="pro-bio">{pp.extended_bio}</p>
        )}

        <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-500">
          {pp.city && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {pp.city}{pp.country ? `, ${pp.country}` : ""}
            </span>
          )}
          {typeof data.audience_count === "number" && (
            <span data-testid="pro-audience-count">Audience: <strong>{data.audience_count}</strong></span>
          )}
        </div>

        {/* CTAs */}
        <div className="mt-5 grid grid-cols-3 gap-2" data-testid="pro-ctas">
          {data.is_following ? (
            <button
              onClick={doUnfollow}
              disabled={busy}
              className="col-span-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 disabled:opacity-50"
              data-testid="pro-cta-connected"
            >
              Collegato ✓
            </button>
          ) : (
            <button
              onClick={doFollow}
              disabled={busy}
              className="col-span-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
              data-testid="pro-cta-connect"
            >
              Segui
            </button>
          )}
          <button
            onClick={() => document.getElementById("pro-contact-sheet").showModal?.()}
            className="col-span-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50"
            data-testid="pro-cta-contact"
            disabled={!pp.business_email && !pp.business_phone}
          >
            Contatta
          </button>
          <button
            onClick={() => trackClick("website", websiteUrl)}
            className="col-span-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 disabled:opacity-40"
            disabled={!websiteUrl}
            data-testid="pro-cta-website"
          >
            Visita sito
          </button>
        </div>

        {/* Contact sheet */}
        <dialog id="pro-contact-sheet" className="rounded-2xl p-0 backdrop:bg-black/40 w-[min(90vw,360px)]">
          <div className="p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Contatti</h3>
            <div className="space-y-2">
              {pp.business_email && (
                <button
                  onClick={() => trackClick("email", pp.business_email)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 text-left"
                  data-testid="pro-contact-email"
                >
                  <Mail className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-gray-800 truncate">{pp.business_email}</span>
                </button>
              )}
              {pp.business_phone && (
                <button
                  onClick={() => trackClick("phone", pp.business_phone)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 text-left"
                  data-testid="pro-contact-phone"
                >
                  <Phone className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-gray-800">{pp.business_phone}</span>
                </button>
              )}
              {websiteUrl && (
                <button
                  onClick={() => trackClick("website", websiteUrl)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 text-left"
                  data-testid="pro-contact-website"
                >
                  <Globe className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-gray-800 truncate">{pp.website_url}</span>
                </button>
              )}
            </div>
            <form method="dialog" className="mt-4">
              <button className="w-full py-2 rounded-xl bg-gray-900 text-white text-sm font-semibold">Chiudi</button>
            </form>
          </div>
        </dialog>
      </div>
    </div>
  );
}
